import React from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import {
  SlotRenderer,
  BackgroundMediaRenderer,
  EmptyMediaSlotAffordance,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";

/**
 * HeroStack — renderer for ``stack`` (static) and ``stack_animated``
 * (Ken-Burns on top media) modes.
 *
 * Vertical stack: media banner on top spanning the full width, then
 * heading / body / CTA stacked beneath it. The same composition at
 * every breakpoint — there is no horizontal vs vertical split, which
 * makes Stack the most predictable mode for users who want a calm
 * top-to-bottom reading rhythm regardless of viewport.
 *
 * The media block takes a fixed proportion of the hero height
 * (``mediaPct``) so the text column always has room to breathe even
 * when the user picks a tall image. Falls back to a centered text
 * column when no media is present.
 */

const BP_CONFIG = {
  // ``mediaPct`` is the share of the hero height the top banner takes.
  // We keep it at ~55% so the text column always has at least 40% of
  // the hero to itself, regardless of the user's chosen height.
  //
  // ``contentPy`` is the text column's top + bottom padding — on the
  // with-media branch it directly controls the image↔title gap (top
  // pad) and the cta↔hero-bottom gap (bottom pad), because the column
  // is anchored to the top via ``justify-start`` (see render block
  // below). Kept tight so the text reads as a continuation of the
  // banner instead of floating mid-column.
  //
  // ``gap`` is the inter-slot spacing between heading / body / cta.
  // Each slot wrapper additionally carries ``p-2`` (8px top + 8px
  // bottom), so the visual inter-slot distance is ``gap + 16px``.
  // Kept small here so the title / body / cta cluster reads as a
  // single tight composition, not three drifting blocks.
  extra_large: { mediaPct: 0.55, contentPx: 80, contentPy: 16, gap: 4 },
  large:       { mediaPct: 0.55, contentPx: 64, contentPy: 14, gap: 4 },
  medium:      { mediaPct: 0.5,  contentPx: 48, contentPy: 12, gap: 4 },
  small:       { mediaPct: 0.45, contentPx: 24, contentPy: 10, gap: 2 },
  extra_small: { mediaPct: 0.42, contentPx: 16, contentPy: 8,  gap: 2 },
};

const HeroStack = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const mediaSlotData = heroSlots?.getSlot("media");
  const hasMedia = !!mediaSlotData;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Ken-Burns on the top media (mode-owned via ``stack_animated``).
  const mediaAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 150 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 300 });

  // No media → centered single-column layout. Same graceful-degrade
  // pattern as Magazine/Split; lets users pick Stack mode before
  // assigning a banner image without seeing a broken empty top half.
  if (!hasMedia) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        <Background
          bgImage={heroBase.bgImage}
          imageStyleClasses={heroBase.imageStyleClasses}
          imageInlineStyles={heroBase.imageInlineStyles}
          colorClass={heroBase.colorClass}
          colorStyle={heroBase.colorStyle}
          jsPatternLayer={heroBase.jsPatternLayer}
        />
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            className="flex flex-col gap-4 w-full max-w-2xl mx-auto"
            style={{ padding: config.contentPx, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty media
                banner slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="media"
              order={0}
            />
            {/* ``p-2`` (8px on every side) gives each slot wrapper a
                small inner gutter so the title / body / CTA never
                visually butt against the column's outer padding edge.
                Mirrored on the with-media branch below so both Stack
                code paths land at the same per-slot breathing room. */}
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} className="p-2" />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} className="p-2" />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} className="p-2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`flex flex-col w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Media banner. Fixed share of the hero height — the rest
            goes to the text column. ``min-h-0`` is needed so flexbox
            doesn't try to size the column to its intrinsic content. */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={{ width: "100%", height: `${Math.round(baseHeightPx * config.mediaPct)}px`, minHeight: 0 }}
        >
          <BackgroundMediaRenderer
            slotData={mediaSlotData}
            heroBase={heroBase}
            ambientClass={mediaAmbientClass}
            heroItem={item}
          />
        </div>

        {/* Text column beneath the banner. Anchored to the TOP of the
            available space (``justify-start``) — not vertically
            centered — so the title sits right under the banner with
            just ``contentPy`` worth of breathing room above it.
            ``justify-center`` (the previous behaviour) made the
            image↔title gap balloon to ``(columnHeight - clusterHeight)
            / 2`` because the leftover column space split evenly above
            and below the centered title/body/cta cluster — on a
            tall hero with a short text cluster this read as the
            banner and the title floating apart with no visual
            relationship. Anchoring to the start of the column makes
            the text continue the banner immediately; any leftover
            column space accumulates harmlessly at the bottom (where
            the hero's outer container bottom-pad already lives). */}
        <div
          className="flex flex-col flex-1 justify-start"
          style={{
            paddingLeft: config.contentPx,
            paddingRight: config.contentPx,
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            gap: config.gap,
            ...contentInsetStyle,
          }}
        >
          {/* ``p-2`` (8px on every side) on each slot wrapper — see
              the parallel comment on the no-media branch above. The
              column's outer ``contentPx`` / ``contentPy`` already
              insets the entire text block from the hero edges; this
              extra per-slot gutter is a separate inner buffer so a
              long title or a wide button row never visually butts
              against the column's resolved padding edge. */}
          <div ref={headingAnim.ref} className={headingAnim.className} style={headingAnim.style}>
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} className="p-2" />
          </div>
          <div ref={bodyAnim.ref} className={bodyAnim.className} style={bodyAnim.style}>
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} className="p-2" />
          </div>
          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} className="p-2" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroStack);
